import "./QuoteData.scss";
import React, { useMemo } from "react";
import { FormOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import {
  ApplicationResponse,
  dealFundOptions,
  getApplicationSelector,
} from "@quest-finance/quest-fe-shared/dist/application";
import { AMOUNT_TYPES } from "@quest-finance/quest-fe-shared/dist/application/constants/amountTypes";
import { REPAYMENT_TERM_OPTION_LABELS } from "@quest-finance/quest-fe-shared/dist/application/constants/repaymentTermOptions";
import { SUPPLIER_TYPE_LABELS } from "@quest-finance/quest-fe-shared/dist/application/constants/supplierTypes";
import {
  getBrokeragePercentage,
  getFutureValuePercentage,
} from "@quest-finance/quest-fe-shared/dist/application/utils/quote";
import { Col, Row, Collapse, Typography, Divider, Popover } from "antd";
import { NumericFormat } from "react-number-format";
import { useSelector } from "react-redux";
import { Link } from "react-router";
import { QfCollapse } from "../../../theme";
import { resolveAssetLabels } from "../../utils/security";

const { Text } = Typography;
const { Panel } = Collapse;
const { coFund, DEAL_FUND_OPTIONS } = dealFundOptions;

const QuoteData: React.FunctionComponent = () => {
  const application = useSelector(
    getApplicationSelector
  ) as ApplicationResponse;
  const { quote } = application;
  const { assetCategoryLabel, assetTypeLabel } = resolveAssetLabels(quote);
  const { introducerFundPercentage, lenderName } = application ?? {};

  const balloonPercentage = useMemo(() => {
    return quote.balloonType === AMOUNT_TYPES.PERCENTAGE
      ? quote.balloonAmount
      : getFutureValuePercentage(quote.purchaseAmount, quote.balloonAmount);
  }, [quote]);

  const brokeragePercentage = useMemo(() => {
    return quote.brokerageType === AMOUNT_TYPES.PERCENTAGE
      ? quote.brokerageAmount
      : getBrokeragePercentage(quote.amountFinanced, quote.brokerageAmount);
  }, [quote]);

  const netAmountFinanced = useMemo(() => {
    return (
      quote.purchaseAmount -
      quote.depositAmount -
      quote.tradeInAmount +
      quote.tradePayoutAmount
    );
  }, [quote]);

  const financedFees = useMemo(() => {
    if (quote.includeApplicationFee && quote.includeOriginationFee) {
      return (
        quote.applicationFeeWithGst + quote.brokerOriginationFeeAmountWithGst
      );
    } else if (quote.includeApplicationFee) {
      return quote.applicationFeeWithGst;
    } else if (quote.includeOriginationFee) {
      return quote.brokerOriginationFeeAmountWithGst;
    }
  }, [quote]);

  const funder =
    DEAL_FUND_OPTIONS.find((entry) => entry.value === lenderName)?.label ?? "-";

  const quotesLink = `/application/applications/${application.id}/quotes`;

  return (
    <QfCollapse
      className="quote-expand"
      defaultActiveKey="quote-data"
      expandIconPosition="end"
      ghost
    >
      <Panel
        header={<div className="section-header-1">Quote Data</div>}
        key="quote-data"
        className="application-data"
      >
        <div className="quote-wrapper">
          <h6 className="header">
            Quote{" "}
            <Link to={quotesLink}>
              <FormOutlined className="edit-link" />
            </Link>
          </h6>
          <Row gutter={[32, 16]}>
            <Col span={24} md={6}>
              <h6 className="section-header">Funding Options</h6>
              <div className="details mb-3">
                <div className="info">
                  <Text strong className="label">
                    Who will be funding this deal?
                  </Text>
                  <div>{funder}</div>
                </div>
                {lenderName === coFund && (
                  <div className="info">
                    <Text strong className="label">
                      I will contribute
                    </Text>
                    <div>
                      {introducerFundPercentage
                        ? introducerFundPercentage + "%"
                        : "-"}
                    </div>
                  </div>
                )}
              </div>
              <h6 className="section-header">Asset Details</h6>
              <div className="info">
                <Text strong className="label">
                  Category
                </Text>
                <div>{assetCategoryLabel}</div>
              </div>
              <div className="info">
                <Text strong className="label">
                  Type
                </Text>
                <div>{assetTypeLabel}</div>
              </div>
              <div className="info">
                <Text strong className="label">
                  Year
                </Text>
                <div>{quote.assetManufactureYear}</div>
              </div>
              <div className="info">
                <Text strong className="label">
                  Supplier
                </Text>
                <div>
                  {SUPPLIER_TYPE_LABELS[quote.supplierType as string] as string}
                </div>
              </div>
              <div className="info">
                <Text strong className="label">
                  Property owner:
                </Text>
                <div>{quote.isPropertyOwner ? "Yes" : "No"}</div>
              </div>
            </Col>
            <Col span={24} md={6}>
              <h6 className="section-header">Fees and Rates</h6>
              <div className="info">
                <Text strong className="label">
                  Term (years)
                </Text>
                <div>{quote.repaymentTermMonth / 12}</div>
              </div>
              <div className="info">
                <Text strong className="label">
                  Brokerage % (ex GST)
                </Text>
                <div>
                  <NumericFormat
                    value={brokeragePercentage}
                    displayType="text"
                    fixedDecimalScale
                    thousandSeparator=","
                    decimalScale={2}
                    suffix="%"
                  />
                </div>
              </div>
              <div className="info">
                <Text strong className="label">
                  Brokerage $ (ex GST)
                </Text>
                <div>
                  <NumericFormat
                    value={quote.brokerageNominal}
                    prefix="$"
                    displayType="text"
                    fixedDecimalScale
                    thousandSeparator=","
                    decimalScale={2}
                  />
                </div>
              </div>
              <div className="info">
                <Text strong className="label">
                  Risk fee $ (ex GST)
                </Text>
                <div>
                  <NumericFormat
                    value={quote.riskFeeAmount}
                    prefix="$"
                    displayType="text"
                    fixedDecimalScale
                    thousandSeparator=","
                    decimalScale={2}
                  />
                </div>
              </div>
              <div className="info">
                <Text strong className="label">
                  Financier rate
                </Text>
                <div>
                  <NumericFormat
                    value={quote.financierRate}
                    displayType="text"
                    fixedDecimalScale
                    thousandSeparator=","
                    decimalScale={2}
                    suffix={"%"}
                  />
                </div>
              </div>
              <div className="info">
                <Text strong className="label">
                  Base rate
                </Text>
                <div>
                  <NumericFormat
                    value={quote.baseRate}
                    displayType="text"
                    fixedDecimalScale
                    thousandSeparator=","
                    decimalScale={2}
                    suffix={"%"}
                  />
                </div>
              </div>
              <div className="info">
                <Text strong className="label">
                  Application Fee
                </Text>
                <div>{quote.includeApplicationFee ? "Yes" : "No"}</div>
              </div>
              <div className="info">
                <Text strong className="label">
                  Origination Fee
                </Text>
                <div>{quote.includeOriginationFee ? "Yes" : "No"}</div>
              </div>
              <div className="info">
                <Text strong className="label">
                  Manual rate
                </Text>
                <div>{quote.isFinancierRateManual ? "Yes" : "No"}</div>
              </div>
            </Col>
            <Col span={24} md={6}>
              <h6 className="section-header">Financials</h6>
              <table className="numbers-table">
                <tbody>
                  <tr>
                    <td>Purchase price</td>
                    <td className="value">
                      <NumericFormat
                        value={quote.purchaseAmount}
                        prefix="$"
                        displayType="text"
                        fixedDecimalScale
                        thousandSeparator=","
                        decimalScale={2}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>Cash deposit</td>
                    <td className="value">
                      (
                      <NumericFormat
                        value={quote.depositAmount}
                        prefix="$"
                        displayType="text"
                        fixedDecimalScale
                        thousandSeparator=","
                        decimalScale={2}
                      />
                      )
                    </td>
                  </tr>
                  <tr>
                    <td>Trade in</td>
                    <td className="value">
                      (
                      <NumericFormat
                        value={quote.tradeInAmount}
                        prefix="$"
                        displayType="text"
                        fixedDecimalScale
                        thousandSeparator=","
                        decimalScale={2}
                      />
                      )
                    </td>
                  </tr>
                  <tr>
                    <td>Payout amount</td>
                    <td className="value">
                      <NumericFormat
                        value={quote.tradePayoutAmount}
                        prefix="$"
                        displayType="text"
                        fixedDecimalScale
                        thousandSeparator=","
                        decimalScale={2}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <Divider className="qf-divider" />
              <table className="numbers-table">
                <tbody>
                  <tr>
                    <td>Net purchase price</td>
                    <td className="value">
                      <NumericFormat
                        value={netAmountFinanced}
                        prefix="$"
                        displayType="text"
                        fixedDecimalScale
                        thousandSeparator=","
                        decimalScale={2}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>Financed fees</td>
                    <td className="value">
                      <NumericFormat
                        value={financedFees}
                        prefix="$"
                        displayType="text"
                        fixedDecimalScale
                        thousandSeparator=","
                        decimalScale={2}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <Divider className="qf-divider" />
              <table className="numbers-table">
                <tbody>
                  <tr>
                    <td>Net amount financed (NAF)</td>
                    <td className="value">
                      <NumericFormat
                        value={quote.amountFinanced}
                        prefix="$"
                        displayType="text"
                        fixedDecimalScale
                        thousandSeparator=","
                        decimalScale={2}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <Divider className="qf-divider" />
              <table className="numbers-table">
                <tbody>
                  <tr>
                    <td>Origination fee (ex GST)</td>
                    <td className="value">
                      <NumericFormat
                        value={quote.brokerOriginationFeeAmount}
                        prefix="$"
                        displayType="text"
                        fixedDecimalScale
                        thousandSeparator=","
                        decimalScale={2}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>Estab fee (ex GST)</td>
                    <td className="value">
                      <NumericFormat
                        value={quote.applicationFee}
                        prefix="$"
                        displayType="text"
                        fixedDecimalScale
                        thousandSeparator=","
                        decimalScale={2}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </Col>
            <Col span={24} md={6}>
              <h6 className="section-header">Summary</h6>
              <table className="numbers-table">
                <tbody>
                  <tr>
                    <td>
                      {
                        REPAYMENT_TERM_OPTION_LABELS[
                          quote.repaymentTermOption
                        ] as string
                      }{" "}
                      repayments{" "}
                    </td>
                    <td className="value">
                      <NumericFormat
                        value={quote.installmentAmount}
                        prefix="$"
                        displayType="text"
                        fixedDecimalScale
                        thousandSeparator=","
                        decimalScale={2}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>First instalment</td>
                    <td className="value">
                      <NumericFormat
                        value={quote.firstInstallmentAmount}
                        prefix="$"
                        displayType="text"
                        fixedDecimalScale
                        thousandSeparator=","
                        decimalScale={2}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>Balloon </td>
                    <td className="value">
                      (
                      <NumericFormat
                        value={balloonPercentage}
                        displayType="text"
                        fixedDecimalScale
                        thousandSeparator=","
                        decimalScale={2}
                        suffix="%"
                      />
                      ){" "}
                      <NumericFormat
                        value={quote.balloonNominal}
                        prefix="$"
                        displayType="text"
                        fixedDecimalScale
                        thousandSeparator=","
                        decimalScale={2}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>Total payment to broker(inc GST) </td>
                    <td className="value">
                      <NumericFormat
                        value={quote.totalPaymentToBrokerWithGst}
                        prefix="$"
                        displayType="text"
                        fixedDecimalScale
                        thousandSeparator=","
                        decimalScale={2}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Amount funded
                      <Popover
                        content="NAF + Brokerage (inc GST) + Risk fee (inc GST)"
                        title={null}
                        placement="top"
                      >
                        <QuestionCircleOutlined className="popover-icon" />
                      </Popover>
                    </td>
                    <td className="value">
                      <NumericFormat
                        value={
                          quote.amountFinanced +
                          quote.brokerageNominalWithGst +
                          quote.riskFeeAmountWithGst
                        }
                        prefix="$"
                        displayType="text"
                        fixedDecimalScale
                        thousandSeparator=","
                        decimalScale={2}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      Amount to be disbursed
                      <Popover
                        content="Net purchase price + Total payment to broker (inc GST) + Risk fee (inc GST)"
                        title={null}
                        placement="top"
                      >
                        <QuestionCircleOutlined className="popover-icon" />
                      </Popover>
                    </td>
                    <td className="value">
                      <NumericFormat
                        value={
                          netAmountFinanced +
                          quote.totalPaymentToBrokerWithGst +
                          quote.riskFeeAmountWithGst
                        }
                        prefix="$"
                        displayType="text"
                        fixedDecimalScale
                        thousandSeparator=","
                        decimalScale={2}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </Col>
          </Row>
        </div>
      </Panel>
    </QfCollapse>
  );
};

export default QuoteData;
